import { createElement, memo, useMemo, type CSSProperties } from 'react';

import { cn } from '~/utils/cn';

import type { PropsText } from './interfaces';

type Token = { text: string; bold: boolean };

function tokenizeBold(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  let buffer = '';
  let inBold = false;

  const flush = () => {
    if (buffer) {
      tokens.push({ text: buffer, bold: inBold });
      buffer = '';
    }
  };

  while (i < input.length) {
    if (input.charCodeAt(i) === 42 && input.charCodeAt(i + 1) === 42) {
      flush();
      inBold = !inBold;
      i += 2;
      continue;
    }
    buffer += input[i];
    i += 1;
  }

  if (inBold) return [{ text: input, bold: false }];
  flush();
  return tokens;
}

export const Text = memo(function Text({
  children,
  className,
  numberOfLines,
  onClick,
  as = 'span',
  htmlFor,
  title,
  inline,
}: PropsText) {
  const content = typeof children === 'string' ? children : null;

  const tokens = useMemo(() => {
    if (content == null || content.length === 0) return null;
    if (content.indexOf('**') === -1) return null;
    return tokenizeBold(content);
  }, [content]);

  const lineClampStyle: CSSProperties | undefined =
    numberOfLines && numberOfLines > 0
      ? {
          display: '-webkit-box',
          WebkitLineClamp: numberOfLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }
      : undefined;

  const inner = tokens
    ? tokens.map((t, idx) =>
        t.bold ? (
          <strong key={idx} className="font-bold">
            {t.text}
          </strong>
        ) : (
          <span key={idx}>{t.text}</span>
        ),
      )
    : children;

  return createElement(
    as,
    {
      className: cn(
        'font-sans',
        !inline && 'block',
        onClick && 'cursor-pointer',
        className,
      ),
      onClick,
      style: lineClampStyle,
      htmlFor,
      title,
    },
    inner,
  );
});
